import { RunnerCase, RunnerStep } from '../../models/test-run.model';
import { isOpenStatus } from '../../shared/test-run-outcome';

export { isOpenStatus as isOpen } from '../../shared/test-run-outcome';

/**
 * Die auswertbaren Schritte eines Durchlaufs in Ausfuehrungsreihenfolge.
 *
 * <p>Nur Blaetter (`kind === 'STEP'`) zaehlen; Gruppen- und Iterations-Knoten
 * tragen kein eigenes Ergebnis. Genau diese Regel wendet der Server in
 * `RunnerStateDTO.of` an, wenn er `stepsPassed`/`stepsTotal` bildet.
 *
 * <p>Deshalb steht sie hier und nicht in den Komponenten: Seite und Manuell-Runner
 * zaehlen beide daraus — die Kopfzahl des Fortschritts und der Schritt-Bezug des
 * Pause-Banners. Liefen die Regeln auseinander, meldete dieselbe Antwort zwei
 * verschiedene Gesamtzahlen.
 */
export function leafSteps(cases: RunnerCase[]): RunnerStep[] {
  return cases.flatMap((runnerCase) => runnerCase.steps).filter((step) => step.kind === 'STEP');
}

/**
 * Die bereits bewerteten Schritte — alles ausser offen und laufend, wie `decided` im
 * Entwurf (`page-testlauf.jsx:172`). Daraus rechnet der Kopf „abgeschlossen": wer
 * alles bewertet hat, ist fertig, auch wenn nicht alles bestanden hat (NEX-1496).
 */
export function decidedSteps(cases: RunnerCase[]): RunnerStep[] {
  return leafSteps(cases).filter((step) => !isOpenStatus(step.actualStatus));
}

/**
 * Ob ein Fall etwas zu bewerten hat: jede Zeile ausser Modulknoten. Das ist die Regel von
 * `TestRunRollup.hasLeaves` am Server, nicht die von {@link leafSteps} (nur `STEP`, fuer die
 * Zaehlung). Ein Fall ohne solche Zeile gilt als „ohne Schritte": der Runner springt ihn nicht an,
 * und der Server zaehlt ihn beim Abschluss als uebersprungen (NEX-1673).
 */
export function hasRatableSteps(runnerCase: RunnerCase): boolean {
  return runnerCase.steps.some((step) => step.kind !== 'MODULE' || isOpenCall(step));
}

/**
 * Irgendwo im Lauf steht noch ein Schritt ohne Ergebnis. Solange das so ist, schliesst der Server
 * den Fall als laufend ab; deshalb sperrt dieselbe Regel hier den Abschluss (NEX-1673).
 */
export function hasOpenRatableStep(cases: RunnerCase[]): boolean {
  return cases.some((runnerCase) =>
    runnerCase.steps.some((step) => step.kind !== 'MODULE' && isOpenStatus(step.actualStatus)),
  );
}

/** Ein Modulaufruf, ueber den die Testerin noch entscheiden muss (NEX-2280). */
export function isOpenCall(step: RunnerStep | undefined | null): boolean {
  return step?.callState === 'OPEN';
}

/**
 * Die Schritte eines Falls nach einer Entscheidung: der Aufruf in seiner neuen Form, die neuen
 * Zeilen an ihrer Stelle, alles ab der Einfuegestelle rueckt um ihre Zahl nach hinten - dieselbe
 * Regel wie am Server. Zeilen, die schon da sind (ein Neuladen nach `call-expanded` kam der
 * Antwort zuvor), kommen nicht ein zweites Mal hinein.
 */
export function applyCallDecision(steps: RunnerStep[], call: RunnerStep, added: RunnerStep[]): RunnerStep[] {
  const known = new Set(steps.map((step) => step.id));
  const fresh = added.filter((step) => !known.has(step.id));
  const replaced = steps.map((step) => (step.id === call.id ? { ...call, orderIndex: step.orderIndex } : step));
  if (fresh.length === 0) return replaced;
  const insertAt = fresh[0].orderIndex;
  const shifted = replaced.map((step) =>
    step.orderIndex >= insertAt ? { ...step, orderIndex: step.orderIndex + fresh.length } : step,
  );
  return [...shifted, ...fresh].sort((a, b) => a.orderIndex - b.orderIndex);
}
